/** Pure jellyfish AI target-tile selection + intersection routing (unit tested).
 * These mirror the classic Pac-Man ghost behaviours. */
import { Direction, DIR_DELTA, OPPOSITE, ALL_DIRS, GridPos } from '../types'

const dist2 = (a: GridPos, b: GridPos): number => {
  const dr = a.row - b.row
  const dc = a.col - b.col
  return dr * dr + dc * dc
}

/** Chaser (Blinky): aim straight at the player's tile. */
export function chaserTarget(player: GridPos): GridPos {
  return { row: player.row, col: player.col }
}

/** Ambusher (Pinky): aim `ahead` tiles in front of the player's facing. */
export function ambusherTarget(player: GridPos, facing: Direction, ahead: number): GridPos {
  const d = DIR_DELTA[facing]
  return { row: player.row + d.dr * ahead, col: player.col + d.dc * ahead }
}

/** Patroller (Inky): pivot `ahead` in front of player, then double the chaser→pivot vector (flank). */
export function patrollerTarget(
  player: GridPos,
  facing: Direction,
  chaser: GridPos,
  ahead = 2,
): GridPos {
  const d = DIR_DELTA[facing]
  const pivot = { row: player.row + d.dr * ahead, col: player.col + d.dc * ahead }
  return { row: pivot.row + (pivot.row - chaser.row), col: pivot.col + (pivot.col - chaser.col) }
}

/** Wanderer (Clyde): chase when far, but retreat to its scatter corner when within threshold. */
export function wandererTarget(
  self: GridPos,
  player: GridPos,
  corner: GridPos,
  threshold: number,
): GridPos {
  return dist2(self, player) > threshold * threshold
    ? { row: player.row, col: player.col }
    : { row: corner.row, col: corner.col }
}

/** Pac-Man tie-break priority when distances are equal. */
const PRIORITY: Direction[] = [Direction.Up, Direction.Left, Direction.Down, Direction.Right]

/**
 * At an intersection choose the direction that minimises distance to `target`,
 * never reversing (unless it's the only option, e.g. a dead-end).
 */
export function chooseDirAtNode(
  cell: GridPos,
  currentDir: Direction,
  target: GridPos,
  isWalkable: (row: number, col: number) => boolean,
): Direction {
  const reverse = OPPOSITE[currentDir]
  const forward = ALL_DIRS.filter((d) => d !== reverse).filter((d) => {
    const nd = DIR_DELTA[d]
    return isWalkable(cell.row + nd.dr, cell.col + nd.dc)
  })
  const pool = forward.length
    ? forward
    : ALL_DIRS.filter((d) => {
        const nd = DIR_DELTA[d]
        return isWalkable(cell.row + nd.dr, cell.col + nd.dc)
      })
  if (!pool.length) return Direction.None

  let best = pool[0]
  let bestDist = Infinity
  let bestPri = 99
  for (const d of pool) {
    const nd = DIR_DELTA[d]
    const next = { row: cell.row + nd.dr, col: cell.col + nd.dc }
    const dd = dist2(next, target)
    const pri = PRIORITY.indexOf(d)
    if (dd < bestDist - 1e-9 || (Math.abs(dd - bestDist) < 1e-9 && pri < bestPri)) {
      best = d
      bestDist = dd
      bestPri = pri
    }
  }
  return best
}

/** Pick a random walkable, non-reversing direction (frightened wandering). */
export function randomDirAtNode(
  cell: GridPos,
  currentDir: Direction,
  isWalkable: (row: number, col: number) => boolean,
  rng: () => number = Math.random,
): Direction {
  const reverse = OPPOSITE[currentDir]
  const options = ALL_DIRS.filter((d) => d !== reverse).filter((d) => {
    const nd = DIR_DELTA[d]
    return isWalkable(cell.row + nd.dr, cell.col + nd.dc)
  })
  const pool = options.length
    ? options
    : ALL_DIRS.filter((d) => {
        const nd = DIR_DELTA[d]
        return isWalkable(cell.row + nd.dr, cell.col + nd.dc)
      })
  if (!pool.length) return Direction.None
  return pool[Math.floor(rng() * pool.length)]
}
